// src/pages/Admin/Photos/PhotosAdmin.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiFileText,
  FiImage,
  FiUploadCloud,
  FiLock,
  FiGlobe,
} from "react-icons/fi";

import {
  searchMedia,
  uploadMedia,
  updateMedia,
  deleteMedia,
  getMediaCategories,
  isImageMedia,
  isPdfMedia,
} from "../../../services/mediaLibraryServices";

import PhotosFilters from "./components/PhotosFilters";
import PhotosList from "./components/PhotosList";
import PhotosFormModal from "./components/PhotosFormModal";
import PhotosPreviewModal from "./components/PhotosPreviewModal";
import ConfirmModal from "./components/ConfirmModal";

export default function PhotosAdmin() {
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [visibilityFilter, setVisibilityFilter] = useState("all");

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(
    () => Math.max(Math.ceil(total / limit) || 1, 1),
    [total, limit],
  );

  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create");
  const [saving, setSaving] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  const [categoryOptions, setCategoryOptions] = useState([]);

  const [form, setForm] = useState({
    title: "",
    alt_text: "",
    category: "",
    tags: "",
    type: "image",
    is_public: true,
  });

  const [file, setFile] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const pageStats = useMemo(() => {
    const images = items.filter(isImageMedia).length;
    const documents = items.filter(isPdfMedia).length;
    const publics = items.filter((item) => !!item.is_public).length;
    const privates = items.length - publics;

    return {
      images,
      documents,
      publics,
      privates,
    };
  }, [items]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        q: q.trim() || undefined,
        category:
          categoryFilter === "all" || !categoryFilter.trim()
            ? undefined
            : categoryFilter.trim(),
        type:
          typeFilter === "all" || !typeFilter.trim()
            ? undefined
            : typeFilter.trim(),
        is_public:
          visibilityFilter === "all"
            ? undefined
            : visibilityFilter === "public",
        page,
        limit,
        sort: "created_at",
        dir: "DESC",
      };

      const res = await searchMedia(params);

      setItems(res.items || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error("Erreur chargement médiathèque:", e);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, categoryFilter, typeFilter, visibilityFilter, page, limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmitSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    fetchData();
  };

  const fetchCategories = useCallback(async () => {
    const categories = await getMediaCategories();
    setCategoryOptions(categories);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const onResetFilters = () => {
    setQ("");
    setCategoryFilter("all");
    setTypeFilter("all");
    setVisibilityFilter("all");
    setLimit(24);
    setPage(1);
  };

  const openCreate = () => {
    setCurrentItem(null);
    setFormMode("create");
    setForm({
      title: "",
      alt_text: "",
      category: "",
      tags: "",
      type: "image",
      is_public: true,
    });
    setFile(null);
    setFormOpen(true);
  };

  const openEdit = (item) => {
    if (!item) return;

    setCurrentItem(item);
    setFormMode("edit");
    setForm({
      title: item.title || "",
      alt_text: item.alt_text || "",
      category: item.category || "",
      tags: item.tags || "",
      type: item.type || "image",
      is_public: !!item.is_public,
    });
    setFile(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setCurrentItem(null);
    setFile(null);
  };

  const onSave = async () => {
    try {
      setSaving(true);

      if (formMode === "create") {
        if (!file) {
          alert("Veuillez sélectionner une image ou un PDF.");
          return;
        }

        await uploadMedia(file, {
          title: form.title,
          alt_text: form.alt_text,
          category: form.category,
          tags: form.tags,
          type: form.type,
          is_public: form.is_public,
        });
      }

      if (formMode === "edit" && currentItem?.id) {
        await updateMedia(currentItem.id, {
          title: form.title,
          alt_text: form.alt_text,
          category: form.category,
          tags: form.tags,
          type: form.type,
          is_public: form.is_public,
        });
      }

      closeForm();
      await fetchData();
      await fetchCategories();
    } catch (e) {
      console.error("Erreur sauvegarde média:", e);
      alert(
        e?.response?.data?.error ||
          "Une erreur est survenue lors de l’enregistrement du média.",
      );
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteMedia(id);
      setConfirmId(null);
      await fetchData();
      await fetchCategories();
    } catch (e) {
      console.error("Erreur suppression média:", e);
      alert("Une erreur est survenue lors de la suppression du média.");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 text-slate-950 dark:bg-slate-950 dark:text-white md:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <div className="relative overflow-hidden bg-slate-950 px-5 py-6 text-white sm:px-7">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.32),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(34,197,94,0.22),transparent_30%)]" />

            <div className="relative flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold text-sky-100">
                  <FiUploadCloud className="h-4 w-4" />
                  Images, PDF et documents publics
                </div>

                <h1 className="text-2xl font-bold tracking-tight md:text-4xl">
                  Médiathèque
                </h1>

                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">
                  Centralisez les visuels du site, les comptes-rendus du conseil
                  municipal, les publications officielles et les documents à
                  rendre accessibles aux habitants.
                </p>
              </div>

              <button
                type="button"
                onClick={openCreate}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-4 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-black/20 transition hover:-translate-y-0.5 hover:bg-sky-50"
              >
                <FiUploadCloud className="h-5 w-5 text-sky-600" />
                Ajouter un média
              </button>
            </div>
          </div>

          <div className="grid gap-3 border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/70 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/70">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Total filtré
                </p>
                <FiUploadCloud className="h-5 w-5 text-sky-500" />
              </div>
              <p className="mt-2 text-2xl font-bold">{total}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/70">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Images page
                </p>
                <FiImage className="h-5 w-5 text-emerald-500" />
              </div>
              <p className="mt-2 text-2xl font-bold">{pageStats.images}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/70">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Documents page
                </p>
                <FiFileText className="h-5 w-5 text-amber-500" />
              </div>
              <p className="mt-2 text-2xl font-bold">{pageStats.documents}</p>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/70">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Visibilité page
                </p>
                <div className="flex items-center gap-1">
                  <FiGlobe className="h-4 w-4 text-emerald-500" />
                  <FiLock className="h-4 w-4 text-amber-500" />
                </div>
              </div>
              <p className="mt-2 text-sm font-bold">
                {pageStats.publics} publics · {pageStats.privates} privés
              </p>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <PhotosFilters
              q={q}
              setQ={setQ}
              categoryFilter={categoryFilter}
              setCategoryFilter={setCategoryFilter}
              categoryOptions={categoryOptions}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              visibilityFilter={visibilityFilter}
              setVisibilityFilter={setVisibilityFilter}
              limit={limit}
              setLimit={(n) => {
                setLimit(n);
                setPage(1);
              }}
              onSubmit={onSubmitSearch}
              onReset={onResetFilters}
              loading={loading}
              total={total}
            />

            <PhotosList
              items={items}
              loading={loading}
              total={total}
              page={page}
              totalPages={totalPages}
              setPage={setPage}
              onPreview={(item) => setPreviewItem(item)}
              onEdit={openEdit}
              onAskDelete={(id) => setConfirmId(id)}
            />
          </div>
        </div>
      </div>

      <PhotosFormModal
        open={formOpen}
        onClose={closeForm}
        form={form}
        setForm={setForm}
        file={file}
        setFile={setFile}
        onSave={onSave}
        saving={saving}
        mode={formMode}
      />

      <PhotosPreviewModal
        open={!!previewItem}
        onClose={() => setPreviewItem(null)}
        item={previewItem}
      />

      <ConfirmModal
        open={!!confirmId}
        title="Supprimer le média"
        message="Confirmez-vous la suppression de ce média ? Le fichier sera supprimé du serveur et cette action est irréversible."
        onCancel={() => setConfirmId(null)}
        onConfirm={() => onDelete(confirmId)}
      />
    </div>
  );
}
